import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../shared/lib/supabase'
import { useAuth } from './AuthContext'
import { useOrg } from './OrgContext'
import { logAuditEvent } from '../shared/lib/auditLog'

const PolicyContext = createContext(null)

export function PolicyProvider({ children }) {
  const { user } = useAuth()
  const { currentOrg } = useOrg() || {}
  const [templates, setTemplates] = useState([])
  const [globalTemplates, setGlobalTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [clientPolicies, setClientPolicies] = useState({})
  const [policyVersions, setPolicyVersions] = useState({})

  // Org-scoped templates: filter directly by the current org. RLS would
  // catch cross-org access anyway, but filtering server-side keeps the
  // query result tight and gives the global_admin (who sees all orgs via
  // role override) a view scoped to whichever org they're currently in.
  const fetchTemplates = useCallback(async () => {
    if (!user) {
      setTemplates([])
      setLoading(false)
      return
    }
    if (!currentOrg?.id) {
      // No org selected yet — clear and wait for OrgContext to resolve
      setTemplates([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('policy_templates')
        .select('*')
        .eq('global_template', false)
        .eq('org_id', currentOrg.id)
        .order('domain')
        .order('title')

      if (fetchError) throw fetchError
      setTemplates(data || [])
    } catch (err) {
      console.error('Error fetching policy templates:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [user, currentOrg?.id])

  const fetchGlobalTemplates = useCallback(async () => {
    if (!user) {
      setGlobalTemplates([])
      return
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('policy_templates')
        .select('*')
        .eq('global_template', true)
        .order('domain')
        .order('title')

      if (fetchError) throw fetchError
      setGlobalTemplates(data || [])
    } catch (err) {
      console.error('Error fetching global templates:', err)
    }
  }, [user])

  useEffect(() => {
    fetchTemplates()
    fetchGlobalTemplates()
  }, [fetchTemplates, fetchGlobalTemplates])

  const createTemplate = async (data) => {
    // Org-scoped templates require an org_id. Default to currentOrg if the
    // caller didn't explicitly pass one. Global templates (global_template
    // = true) intentionally leave org_id NULL.
    const payload = {
      ...data,
      user_id: user.id,
      org_id: data.global_template
        ? null
        : (data.org_id ?? currentOrg?.id ?? null)
    }

    const { data: inserted, error: insertError } = await supabase
      .from('policy_templates')
      .insert(payload)
      .select()
      .single()

    if (insertError) throw insertError
    if (data.global_template) {
      await fetchGlobalTemplates()
    } else {
      await fetchTemplates()
    }
    return inserted
  }

  const updateTemplate = async (id, data) => {
    const { error: updateError } = await supabase
      .from('policy_templates')
      .update(data)
      .eq('id', id)

    if (updateError) throw updateError
    await fetchTemplates()
  }

  const deleteTemplate = async (id) => {
    const { error: deleteError } = await supabase
      .from('policy_templates')
      .delete()
      .eq('id', id)

    if (deleteError) throw deleteError
    await fetchTemplates()
  }

  const copyToClient = async (templateId, clientId, options = {}) => {
    const template = templates.find(t => t.id === templateId)
    if (!template) throw new Error('Template not found')

    const { data: clientPolicy, error: cpError } = await supabase
      .from('client_policies')
      .insert({
        client_id: clientId,
        template_id: templateId,
        title: template.title,
        domain: template.domain,
        content: template.content,
        structured_content: template.structured_content || null,
        framework_tags: template.framework_tags,
        status: 'draft',
        current_version: 1,
        enrichment_status: options.runEnrichment !== false ? 'pending' : null
      })
      .select()
      .single()

    if (cpError) throw cpError

    const { error: vError } = await supabase
      .from('policy_versions')
      .insert({
        client_policy_id: clientPolicy.id,
        version_number: 1,
        content: template.content,
        change_summary: 'Initial version from template',
        created_by: user.id,
        edit_source: 'org_copy'
      })

    if (vError) throw vError

    logAuditEvent({
      category: 'change',
      action: 'policy.copied',
      description: `Copied policy "${template.title}" to client`,
      clientId,
      resourceType: 'policy',
      resourceId: clientPolicy.id,
      metadata: { templateId, title: template.title, domain: template.domain }
    })

    return clientPolicy
  }

  const copyMultipleToClient = async (templateIds, clientId) => {
    let copiedCount = 0
    for (const templateId of templateIds) {
      await copyToClient(templateId, clientId)
      copiedCount++
    }
    await fetchClientPolicies(clientId)
    return copiedCount
  }

  const fetchClientPolicies = useCallback(async (clientId) => {
    const { data, error: fetchError } = await supabase
      .from('client_policies')
      .select('*')
      .eq('client_id', clientId)
      .order('domain')
      .order('title')

    if (fetchError) throw fetchError
    setClientPolicies(prev => ({ ...prev, [clientId]: data || [] }))
    return data || []
  }, [])

  const updateClientPolicy = async (id, data, changeDescription) => {
    // Fetch the current policy to check if content changed
    const { data: current, error: fetchErr } = await supabase
      .from('client_policies')
      .select('*')
      .eq('id', id)
      .single()

    if (fetchErr) throw fetchErr

    const contentChanged = data.content !== undefined && data.content !== current.content
    const updatePayload = { ...data }

    if (contentChanged) {
      updatePayload.current_version = current.current_version + 1
    }

    const { error: updateError } = await supabase
      .from('client_policies')
      .update(updatePayload)
      .eq('id', id)

    if (updateError) throw updateError

    // If content changed, create a new version record
    if (contentChanged) {
      const { error: vError } = await supabase
        .from('policy_versions')
        .insert({
          client_policy_id: id,
          version_number: current.current_version + 1,
          content: data.content,
          change_summary: changeDescription || 'Content updated',
          created_by: user.id
        })

      if (vError) throw vError
    }

    logAuditEvent({
      category: 'change',
      action: 'policy.updated',
      description: `Updated policy "${current.title}"${contentChanged ? ` (v${current.current_version + 1})` : ''}`,
      clientId: current.client_id,
      resourceType: 'policy',
      resourceId: id,
      metadata: { title: current.title, contentChanged, changeDescription }
    })

    // Refresh the client policies for this client
    await fetchClientPolicies(current.client_id)
  }

  const deleteClientPolicy = async (id) => {
    // Get policy info before deleting so we can refresh and log
    const { data: policy } = await supabase
      .from('client_policies')
      .select('client_id, title')
      .eq('id', id)
      .single()

    const { error: deleteError } = await supabase
      .from('client_policies')
      .delete()
      .eq('id', id)

    if (deleteError) throw deleteError

    logAuditEvent({
      category: 'change',
      action: 'policy.deleted',
      description: `Deleted policy "${policy?.title || id}"`,
      clientId: policy?.client_id,
      resourceType: 'policy',
      resourceId: id
    })

    if (policy) {
      await fetchClientPolicies(policy.client_id)
    }
  }

  const publishClientPolicy = async (id) => {
    const { data: policy } = await supabase
      .from('client_policies')
      .select('client_id, title')
      .eq('id', id)
      .single()

    const { error: updateError } = await supabase
      .from('client_policies')
      .update({ status: 'published', published_at: new Date().toISOString() })
      .eq('id', id)

    if (updateError) throw updateError

    logAuditEvent({
      category: 'change',
      action: 'policy.published',
      description: `Published policy "${policy?.title || id}"`,
      clientId: policy?.client_id,
      resourceType: 'policy',
      resourceId: id
    })

    if (policy) {
      await fetchClientPolicies(policy.client_id)
    }
  }

  const unpublishClientPolicy = async (id) => {
    const { data: policy } = await supabase
      .from('client_policies')
      .select('client_id, title')
      .eq('id', id)
      .single()

    const { error: updateError } = await supabase
      .from('client_policies')
      .update({ status: 'draft', published_at: null })
      .eq('id', id)

    if (updateError) throw updateError

    logAuditEvent({
      category: 'change',
      action: 'policy.unpublished',
      description: `Reverted policy "${policy?.title || id}" to draft`,
      clientId: policy?.client_id,
      resourceType: 'policy',
      resourceId: id
    })

    if (policy) {
      await fetchClientPolicies(policy.client_id)
    }
  }

  const togglePortalVisibility = async (id, visible) => {
    const { data: policy } = await supabase
      .from('client_policies')
      .select('client_id, title')
      .eq('id', id)
      .single()

    const { error: updateError } = await supabase
      .from('client_policies')
      .update({ portal_visible: visible })
      .eq('id', id)

    if (updateError) throw updateError

    logAuditEvent({
      category: 'change',
      action: visible ? 'policy.portal_shown' : 'policy.portal_hidden',
      description: `${visible ? 'Added' : 'Removed'} policy "${policy?.title || id}" ${visible ? 'to' : 'from'} client portal`,
      clientId: policy?.client_id,
      resourceType: 'policy',
      resourceId: id
    })

    if (policy) {
      await fetchClientPolicies(policy.client_id)
    }
  }

  const fetchPolicyVersions = useCallback(async (clientPolicyId) => {
    const { data, error: fetchError } = await supabase
      .from('policy_versions')
      .select('*')
      .eq('client_policy_id', clientPolicyId)
      .order('version_number', { ascending: false })

    if (fetchError) throw fetchError
    setPolicyVersions(prev => ({ ...prev, [clientPolicyId]: data || [] }))
    return data || []
  }, [])

  const restoreVersion = async (clientPolicyId, versionId) => {
    // Get the version to restore
    const { data: version, error: vFetchErr } = await supabase
      .from('policy_versions')
      .select('*')
      .eq('id', versionId)
      .single()

    if (vFetchErr) throw vFetchErr

    // Get current policy
    const { data: policy, error: pFetchErr } = await supabase
      .from('client_policies')
      .select('*')
      .eq('id', clientPolicyId)
      .single()

    if (pFetchErr) throw pFetchErr

    const newVersion = policy.current_version + 1

    // Update the policy content and version
    const { error: updateError } = await supabase
      .from('client_policies')
      .update({
        content: version.content,
        current_version: newVersion
      })
      .eq('id', clientPolicyId)

    if (updateError) throw updateError

    // Create new version record
    const { error: vError } = await supabase
      .from('policy_versions')
      .insert({
        client_policy_id: clientPolicyId,
        version_number: newVersion,
        content: version.content,
        change_summary: `Restored from v${version.version_number}`,
        created_by: user.id
      })

    if (vError) throw vError

    // Refresh data
    await fetchClientPolicies(policy.client_id)
    await fetchPolicyVersions(clientPolicyId)
  }

  return (
    <PolicyContext.Provider value={{
      templates,
      globalTemplates,
      loading,
      error,
      fetchTemplates,
      fetchGlobalTemplates,
      createTemplate,
      updateTemplate,
      deleteTemplate,
      copyToClient,
      copyMultipleToClient,
      clientPolicies,
      fetchClientPolicies,
      updateClientPolicy,
      deleteClientPolicy,
      publishClientPolicy,
      unpublishClientPolicy,
      togglePortalVisibility,
      policyVersions,
      fetchPolicyVersions,
      restoreVersion
    }}>
      {children}
    </PolicyContext.Provider>
  )
}

export function usePolicy() {
  const context = useContext(PolicyContext)
  if (!context) {
    throw new Error('usePolicy must be used within a PolicyProvider')
  }
  return context
}

export default PolicyContext
